/**
 * "What entity is the current page about?" — module-level store published by
 * top-level entity view components (CompanyView, CompanyGroupView,
 * PortfolioView) and read by call sites that want to open the chat with the
 * page's entity binding pre-applied — most notably the top-bar `Ask AI` link
 * (PLT-4665).
 *
 * Why a module-level store rather than React context:
 *
 *   - The reader (top-bar `AskAiLink`) lives in a part of the tree that
 *     wraps the entity views, so a React context defined on the entity-
 *     view side wouldn't be visible to it without lifting the provider far
 *     up the tree (above the router) — which would force every consumer to
 *     remount on every route change.
 *   - This mirrors the existing pattern used by `pageChatContext` and
 *     `hiddenEntityContext` in `upright-chat-shared.ts`: pub-sub state
 *     published by the page, read on demand by the chat plumbing.
 *
 * Lifecycle:
 *
 *   - Entity view components call `usePublishCurrentPageChatBinding(binding)`
 *     on mount; the hook publishes the binding and clears it on unmount.
 *   - `useCurrentPageChatBinding()` returns the currently-published binding
 *     (or `null` on non-entity pages) and re-renders subscribers via
 *     `useSyncExternalStore`.
 *   - The binding intentionally does NOT carry a `href` — that's read
 *     fresh from `window.location` at click time, so a user who clicks
 *     `Ask AI` while on `/company/foo/dma` gets `/company/foo/dma` as the
 *     page-context href, even though the binding was published from the
 *     outer `CompanyView` that doesn't know which sub-route is currently
 *     mounted.
 */
import { useEffect, useSyncExternalStore } from 'react';

export interface CurrentPageChatBinding {
  /** Entity id (e.g. company UUID). Paired with `entityType`. */
  entityId: string;
  /** Entity type — `'company'`, `'group'`, `'portfolio'`. */
  entityType: string;
  /**
   * Human-readable label for the visible page-context chip. Typically the
   * entity name (e.g. `"Neste"`). The Ask AI click handler may append a
   * sub-page descriptor to it (e.g. `"Neste — DMA"`) at click time once we
   * teach it about sub-routes; for v1 just the name is sufficient and
   * already much better than no context at all.
   */
  label: string;
}

let _binding: CurrentPageChatBinding | null = null;
// "Sticky" mirror of the most-recent non-null binding. The publishing entity
// view (`CompanyView` / `CompanyGroupView` / `PortfolioView`) clears the
// live binding on unmount — which is correct for entry-points that need to
// know whether the *current* page has a binding (e.g. the top-bar `Ask AI`
// link), but wrong for the chat-context card: when the user expands the
// side-panel chat to full `/chat`, the entity view unmounts, the live
// binding goes to `null`, and the card would otherwise lose the "Discussing
// {Name}" line mid-conversation. The sticky slot keeps the last seen value
// for the lifetime of the conversation, cleared explicitly on `New chat`.
let _stickyBinding: CurrentPageChatBinding | null = null;
const _listeners = new Set<() => void>();
const _stickyListeners = new Set<() => void>();

function notify(): void {
  for (const l of _listeners) l();
}

function notifySticky(): void {
  for (const l of _stickyListeners) l();
}

/**
 * Replace the currently-published binding. Pass `null` to clear (e.g. when
 * an entity view unmounts).
 *
 * Skips notification if the new value is referentially equal to the
 * previous one — but note that publishers typically pass a fresh object
 * on each render, so equality only kicks in for repeated `null` clears.
 *
 * Updates the sticky slot whenever the new value is non-null. A `null`
 * clear is intentionally NOT mirrored into the sticky slot — that's the
 * whole point of stickiness: the chat-context card should survive a
 * route change that unmounts the publisher.
 */
export function setCurrentPageChatBinding(value: CurrentPageChatBinding | null): void {
  if (_binding === value) return;
  _binding = value;
  notify();
  if (value !== null && _stickyBinding !== value) {
    _stickyBinding = value;
    notifySticky();
  }
}

/** Direct read, for non-React callers. */
export function getCurrentPageChatBinding(): CurrentPageChatBinding | null {
  return _binding;
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeCurrentPageChatBinding(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

/**
 * Reactive accessor for React components — re-renders when the published
 * binding changes.
 */
export function useCurrentPageChatBinding(): CurrentPageChatBinding | null {
  return useSyncExternalStore(subscribeCurrentPageChatBinding, getCurrentPageChatBinding);
}

/** Direct read of the sticky slot, for non-React callers. */
export function getStickyPageChatBinding(): CurrentPageChatBinding | null {
  return _stickyBinding;
}

/**
 * Publish ONLY the sticky binding (the chat-context chip's source), leaving
 * the live binding untouched. Used by deep-link entry points that want the
 * chip to appear in full `/chat` but have no entity view mounted on the chat
 * route to publish a live binding — most notably the DMA landing → `/chat`
 * hand-off (PLT-5051), which carries the company in the URL and wants the
 * same context chip the side-panel contextual chats show.
 *
 * Deliberately does NOT touch `_binding` (the live slot): the chat route has
 * no page publisher, and the live slot is meant to track "does the *current*
 * page have a binding" for entry-points like the top-bar `Ask AI` link, which
 * is irrelevant on `/chat`. Cleared on `New chat` via
 * `resetStickyPageChatBinding`, or on the chip's `×` dismissal.
 */
export function setStickyPageChatBinding(value: CurrentPageChatBinding | null): void {
  if (_stickyBinding === value) return;
  _stickyBinding = value;
  notifySticky();
}

export function subscribeStickyPageChatBinding(listener: () => void): () => void {
  _stickyListeners.add(listener);
  return () => {
    _stickyListeners.delete(listener);
  };
}

/**
 * Reactive accessor that returns the most-recent non-null binding for the
 * lifetime of the current conversation (cleared on `New chat`). Use this
 * from the chat-context card so the chip survives navigating from a
 * panel-bound entity page to full `/chat`. Use `useCurrentPageChatBinding`
 * (live, unmounts with the publisher) for entry-points that need to know
 * "does the *current* page have a binding right now" (e.g. the top-bar
 * `Ask AI` link).
 */
export function useStickyPageChatBinding(): CurrentPageChatBinding | null {
  return useSyncExternalStore(subscribeStickyPageChatBinding, getStickyPageChatBinding);
}

/**
 * Clear the sticky slot. Wired into `resetChatLoggingState` so `New chat`
 * also resets the chip back to "no entity bound" when the user explicitly
 * starts over. Outside that path, the sticky slot is meant to persist:
 * the chat is *still about* the entity even after the user expands the
 * panel to full `/chat`.
 */
export function resetStickyPageChatBinding(): void {
  if (_stickyBinding === null) return;
  _stickyBinding = null;
  notifySticky();
}

/**
 * Helper hook for entity view components. Publishes the binding on mount
 * and clears it on unmount. Pass `null` to skip publishing (typical when
 * the entity name isn't loaded yet — once the name arrives the hook re-
 * runs and publishes).
 *
 * Multiple entity views nested inside each other (which shouldn't happen
 * in practice — the router only mounts one entity view at a time) would
 * stomp on each other's bindings; the most-recently-published one wins
 * until something unmounts, at which point we revert to `null` rather
 * than the previous publisher's value. That's acceptable for the only
 * caller pattern we care about (one entity view active per route).
 */
export function usePublishCurrentPageChatBinding(binding: CurrentPageChatBinding | null): void {
  // Stringify the binding for the dep so re-renders with a fresh object
  // of the same content don't churn the publish/clear cycle. Fields are
  // a small, flat set — JSON.stringify is fine here.
  const key = binding === null ? '' : `${binding.entityType}|${binding.entityId}|${binding.label}`;
  useEffect(() => {
    if (binding === null) {
      setCurrentPageChatBinding(null);
      return;
    }
    setCurrentPageChatBinding(binding);
    return () => {
      // Only clear if our binding is still the active one — if another
      // publisher took over in the meantime (shouldn't happen, see
      // docstring), don't clobber it.
      if (getCurrentPageChatBinding() === binding) {
        setCurrentPageChatBinding(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
